import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { JxtTag, JxtTagSizeEnum } from '@jooxter/ui';
import { ExternalAttendee, ExternalAttendeesService } from '@jooxter/api';
import { createGTMGAEvent, IBookingForm, JxtTagColorsEnum } from '@jooxter/utils';
import { JxtAttendeeCombobox, JxtAttendeeOption, JxtAttendeeOptionTypeEnum } from '../../../../components';
import { IJxtBookingAttendeesField } from './types';

/**
 * Attendee picker. The ids live in the form (`internalAttendees` / `externalAttendees`); `attendees`
 * only carries the labels to display, which is why both are written together here.
 */
export const JxtBookingAttendeesField = ({ attendees, setAttendees }: IJxtBookingAttendeesField) => {
  const { t } = useTranslation();
  const { getValues, setValue, getFieldState } = useFormContext<IBookingForm>();
  const errorMessage = getFieldState('internalAttendees').error?.message;

  const addAttendee = (option: JxtAttendeeOption, field: 'internalAttendees' | 'externalAttendees') => {
    setAttendees([...attendees, option]);
    setValue(field, [...(getValues(field) as number[]), option.id as number], { shouldValidate: true });
  };

  const onAttendeeSelect = (option: JxtAttendeeOption) => {
    if (attendees.some((a) => a.id === option.id && a.type === option.type)) {
      return;
    }

    if (option.type === JxtAttendeeOptionTypeEnum.NEW_EXTERNAL) {
      ExternalAttendeesService.postExternalAttendee({
        email: option.text,
        // Name is required for backwards compatibility with the Angular/mobile application.
        // We may delete this field when the name is no longer required.
        name: option.text,
        disabled: false,
      }).then((attendee: ExternalAttendee) => {
        addAttendee(
          { type: JxtAttendeeOptionTypeEnum.EXTERNAL, id: attendee.id, text: attendee.email },
          'externalAttendees'
        );
        createGTMGAEvent('Booking', 'Create Booking', 'Add External Attendees');
      });
      return;
    }

    if (option.type === JxtAttendeeOptionTypeEnum.EXTERNAL) {
      addAttendee(option, 'externalAttendees');
      createGTMGAEvent('Booking', 'Create Booking', 'Add External Attendees');
      return;
    }

    addAttendee(option, 'internalAttendees');
    createGTMGAEvent('Booking', 'Create Booking', 'Add Internal Attendees');
  };

  const onAttendeeDelete = (option: JxtAttendeeOption) => {
    const field = option.type === JxtAttendeeOptionTypeEnum.EXTERNAL ? 'externalAttendees' : 'internalAttendees';
    setAttendees(attendees.filter((a) => a !== option));
    setValue(
      field,
      getValues(field)?.filter((attendeeId: number) => attendeeId !== option.id),
      { shouldValidate: true }
    );
  };

  return (
    <div className="grid grid-cols-[min-content_minmax(0,1fr)] items-center gap-x-3">
      <i className="fas fa-fw fa-lg fa-user-plus text-neutral-60 col-span-1 col-start-1 row-start-1" />
      <JxtAttendeeCombobox
        placeholder={t<string>('booking.attendees.search.placeholder')}
        onAttendeeSelect={onAttendeeSelect}
        hasErrors={getFieldState('internalAttendees').invalid}
      />
      {errorMessage && typeof errorMessage === 'string' && (
        <p className="text-red-100 pt-1 col-start-2 row-start-3">{errorMessage}</p>
      )}
      {attendees.length > 0 && (
        <div className="flex flex-wrap gap-2 col-start-2 row-start-4 pt-3">
          {attendees.map((a) => (
            <JxtTag
              key={a.type + a.id}
              text={a.text}
              color={JxtTagColorsEnum.BLUE}
              size={JxtTagSizeEnum.LARGE}
              closeable
              onClose={() => onAttendeeDelete(a)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default JxtBookingAttendeesField;
